import React, { useState } from 'react';
import { App, Modal, Table, Checkbox } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import styles from './StoreOrdersList.module.scss';
import { OrderData } from './order';
import {orderStatusOptionsMapper } from '@/utils/constants';
import { useTranslation } from 'react-i18next';
// 新的订单数据类型 - 按照图纸设计


interface StoreOrdersListProps {
  orders: OrderData[];
  selectedOrders: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  onShipmentRequest?: (ids: string[]) => void;
  onVerifyRequest?: (dis: string[]) => void;
  onAfterSalesRequest?: (ids: string[]) => void;
}

const StoreOrdersList: React.FC<StoreOrdersListProps> = ({
  orders,
  selectedOrders,
  onSelectionChange,
  onVerifyRequest,
  onShipmentRequest,
  onAfterSalesRequest
}) => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // 订单详情弹出框状态
  const [orderDetailsModalVisible, setOrderDetailsModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderData | null>(null);

  // 全选/取消全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(orders.map(order => order.product_order_id));
    } else {
      onSelectionChange([]);
    }
  };

  // 单个选择
  const handleSelectOrder = (orderId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedOrders, orderId]);
    } else {
      onSelectionChange(selectedOrders.filter(id => id !== orderId));
    }
  };

  // 查看订单详情
  const handleViewOrder = (order: OrderData) => {
    setSelectedOrder(order);
    setOrderDetailsModalVisible(true);
  };
  // 商家点击校验取货码按钮
  const handlePickupCodeConfirmation = (order: OrderData) => {
    if (onVerifyRequest) {
      onVerifyRequest([order.product_order_id]);
      return;
    }
    message.success(`Processing verify confirmation for order ${order.product_order_id}`);
  }

  // 发货确认（单个）
  const handleShipmentConfirmation = (order: OrderData) => {
    if (onShipmentRequest) {
      onShipmentRequest([order.product_order_id]);
      return;
    }
    message.success(`Processing shipment confirmation for order ${order.product_order_id}`);
  };

  // 售后审核
  const handleAfterSalesReview = (order: OrderData) => {
    if (onAfterSalesRequest) {
      onAfterSalesRequest([order.product_order_id]);
      return;
    }
    message.success(`Processing after-sales review for order ${order.product_order_id}`);
  };

  const isAllSelected = orders.length > 0 && selectedOrders.length === orders.length;
  const isIndeterminate = selectedOrders.length > 0 && selectedOrders.length < orders.length;

  // 定义表格列
  const columns: ColumnsType<OrderData> = [
    {
      title: (
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'checkbox',
      key: 'checkbox',
      width: 50,
      align: 'center',
        render: (_, record) => (
          <Checkbox
            checked={selectedOrders.includes(record.product_order_id)}
            onChange={(e) => handleSelectOrder(record.product_order_id, e.target.checked)}
          />
        ),
    },
    {
      title: t('storeInformation.ProductName'),
      dataIndex: 'productName',
      key: 'productName',
      // width: 280,
      width: 350,
      align: 'left',
      render: (_, record) => {
        const firstItem = record.order_item_list?.[0];
        if (!firstItem) return <span>No product data</span>;
        
        return (
          <div className={styles['product-info']}>
            <img
              src={firstItem.product?.product_media_list?.[0]?.product_media_file_url || ''}
              alt={firstItem.product?.product_title || 'Product'}
              className={styles['product-image']}
            />
            <span className={styles['product-name']} title={firstItem.product?.product_title || 'Product'}>
              {firstItem.product?.product_title || 'Product'}
            </span>
          </div>
        );
      },
    },
    {
      title: t('storeInformation.Price'),
      dataIndex: 'price',
      key: 'price',
      // width: 100,
      width: 120,
      align: 'left',
      render: (_, record) => <span className={styles['price-text']}>${record.order_item_list?.[0]?.product_item_price}</span>,
    },
    {
      title: t('storeInformation.Quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      // width: 80,
      width: 100,
      align: 'left',
      render: (_, record) => <span className={styles['price-text']}>{record.order_item_list?.[0]?.product_item_quantity}</span>
    },
    // {
    //   title: 'Address',
    //   dataIndex: 'address',
    //   key: 'address',
    //   width: 250,
    //   align: 'left',
    //   render: (_, record) => (
    //     <span className={styles['address-text']} title={record.product_shipment_info?.address}>{'none'}</span>
    //   ),
    // },
    {
      title: t('storeInformation.CreationTime'),
      dataIndex: 'created_time',
      key: 'created_time',
      // width: 180,
      width: 200,
      align: 'left',
    },
    {
      title: t('storeInformation.Status'),
      dataIndex: 'status',
      key: 'status',
      // width: 120,
      width: 150,
      align: 'left',
      render: (_, record) => <span>{orderStatusOptionsMapper().find(item => item.value === record.product_order_status)?.label}</span>,

      
    },
    {
      title: t('storeInformation.Operations'),
      key: 'operations',
      // minWidth: 180,
      minWidth: 200,
      align: 'left',
      render: (_, record) => (
        <div className={styles['operation-buttons']}>
          <button
            className={styles['view-btn']}
            onClick={() => handleViewOrder(record)}
          >
            {t('storeOrders.View')}
          </button>
          {/* 商家取货码确认用户已确认收货按钮 */}
          {(record.product_order_status === 3) && (
            <button
              className={styles['shipment-btn']}
              onClick={() => handlePickupCodeConfirmation(record)}
            >
              {t('storeOrders.VerifyPickupCode')}
            </button>
          )}
          {/* {(record.product_order_status === 3) && (
            <button
              className={styles['shipment-btn']}
              onClick={() => handleShipmentConfirmation(record)}
            >
              Shipment confirmation
            </button>
          )} */}

          {/* 售后审核 状态未定 */}
          {/* {(record.product_order_status === 6) && (
            <button
              className={styles['after-sales-btn']}
              onClick={() => handleAfterSalesReview(record)}
            >
              After-sales review
            </button>
          )} */}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className={styles['orders-table-wrapper']}>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="product_order_id"
          pagination={false}
          className={styles['antd-table']}
          scroll={{ x: 1000 }}
          expandable={{
            expandedRowRender: (record) => {
              // 如果订单包含多个商品，展示商品列表
              if (record.order_item_list.length&&record.order_item_list.length>1) {
                return (
                  <div className={styles['expanded-products']}>
                    <p className={styles['products-title']}>All products included in the order:</p>
                    {record.order_item_list.map((item: any, index: number) => (
                      <div key={`${record.product_order_id}-item-${index}`} className={styles['product-item']}>
                        <div className={styles['product-info']}>
                          <img
                            src={item.product?.product_media_list?.[0]?.product_media_file_url}
                            alt={item.product_item_title}
                            className={styles['product-image']}
                          />
                          <div className={styles['product-details']}>
                            <div className={styles['product-name']}>{item.product_item_title}</div>
                            <div className={styles['product-specs']}>
                              {item.product_sku?.specs?.map((spec: any, specIndex: number) => (
                                <span key={`${record.product_order_id}-spec-${specIndex}`} className={styles['spec-item']}>
                                  {spec.lable}: {spec.value}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className={styles['product-price']}>${item.product_item_price}</div>
                        <div className={styles['product-quantity']}>x{item.product_item_quantity}</div>
                        <div className={styles['product-subtotal']}>
                          ${(parseFloat(item.product_item_price) * item.product_item_quantity).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
              return null;
            },
            rowExpandable: (record) => {
              // 只有包含多个商品的订单才可展开
              return !!(record.order_item_list.length&&record.order_item_list.length>1);
            },
            expandRowByClick: false, // 点击行不展开，只能点击展开按钮
          }}
        />
      </div>

      {/* 订单详情弹出框 */}
      <Modal
        title={
          <div style={{
            fontWeight: 'bold',
            fontSize: '24px',
            color: '#0D323E',
            lineHeight: '34px'
          }}>
            Order Details
          </div>
        }
        open={orderDetailsModalVisible}
        onCancel={() => setOrderDetailsModalVisible(false)}
        footer={null}
        width={800}
        className={styles['order-details-modal']}
      >

        {selectedOrder && (
          <div className={styles['order-details-content']}>
            {/* 位置和距离信息 */}
            <div className={styles['location-section']}>
              <div className={styles['location-left']}>
                <img
                  className={styles['product-img']}
                  src={selectedOrder.order_item_list?.[0]?.product?.product_media_list?.[0]?.product_media_file_url || ''}
                  alt={selectedOrder.order_item_list?.[0]?.product?.product_title || 'Product'}
                />
              </div>
              <div className={styles['location-right']}>
                <div className={styles['location-item']}>
                  {selectedOrder.order_item_list?.[0]?.product?.product_title || 'Product'}
                </div>
                {/* <div className={styles['distance-item']}>
                  <span className={styles['distanceIcon']} />
                  {selectedOrder.product_order_extension || '2.5 Miles From You'}
                </div> */}
                <div className={styles['location-description']}>
                  <span>Product Description:</span>
                  <span className={styles['location-description-value']} title={selectedOrder.product_order_remarks || selectedOrder.order_item_list?.[0]?.product?.product_title || 'Product'}>
                    {selectedOrder.product_order_remarks || selectedOrder.order_item_list?.[0]?.product?.product_title || 'Product'}
                  </span>
                </div>
              </div>
            </div>

            {/* 订单信息 */}
            <div className={styles['order-info-section']}>
              <div className={styles['info-row']}>
                <span className={styles['info-label']}>Consignee:</span>
                <span className={styles['info-value']}>{selectedOrder.user?.username || 'Mike'}</span>
              </div>
              <div className={styles['info-row']}>
                <span className={styles['info-label']}>Phone:</span>
                <span className={styles['info-value']}>{selectedOrder.user?.phone || '(910) 722-2019'}</span>
              </div>
              {/* <div className={styles['info-row']}>
                <span className={styles['info-label']}>Address:</span>
                <span className={styles['info-value']}>{selectedOrder.product_shipment_info?.address || 'No address'}</span>
              </div> */}
              <div className={styles['info-row']}>
                <span className={styles['info-label']}>Order Number:</span>
                <span className={styles['info-value']}>{selectedOrder.product_order_id}</span>
              </div>
              <div className={styles['info-row']}>
                <span className={styles['info-label']}>Creation Time:</span>
                <span className={styles['info-value']}>{selectedOrder.created_time}</span>
              </div>
              <div className={styles['info-row']}>
                <span className={styles['info-label']}>Status:</span>
                <span className={styles['info-value']}>{orderStatusOptionsMapper().find(item => item.value === selectedOrder.product_order_status)?.label}</span>
              </div>
              <div className={styles['info-row']}>
                <span className={styles['info-label']}>Unit Price:</span>
                <span className={styles['info-value']}>${selectedOrder.order_item_list?.[0]?.product_item_price || '0.00'}</span>
              </div>
              <div className={styles['info-row']}>
                <span className={styles['info-label']}>Shipping Fee:</span>
                <span className={styles['info-value']}>${'0.00'}</span>
              </div>
              <div className={styles['info-row']}>
                <span className={styles['info-label']}>Quantity:</span>
                <span className={styles['info-value']}>{selectedOrder.order_item_list?.[0]?.product_item_quantity || 0}</span>
              </div>
              <div className={styles['info-row']}>
                <span className={styles['info-label']}>Total Price:</span>
                <span className={styles['info-value']}>${selectedOrder.product_order_total_price}</span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default StoreOrdersList;